/**
 * Per-file "Shared · N" control (multi-client plan). Facilitator-only,
 * on library/restricted file rows: a Radix Popover with one checkbox per
 * client, instant-apply per toggle. Radix's own Popover portals correctly
 * inside the Repository's Dialog layering — do NOT swap this for a
 * createPortal(..., document.body); the Dialog's inert layer eats those
 * (see memory: radix_dialog_portal_inert).
 */

import { Loader2, Share2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import type { useSharing } from "./use-sharing";

interface SharePopoverProps {
  tier: string;
  path: string;
  sharing: ReturnType<typeof useSharing>;
}

export function SharePopover({ tier, path, sharing }: SharePopoverProps) {
  const states = sharing.statesFor(tier, path);
  const count = states.filter((s) => s.exact || s.viaPrefix).length;
  const busy = sharing.toggle.isPending;

  function onToggle(clientId: string, share: boolean) {
    sharing.toggle.mutate(
      { clientId, tier, path, share },
      {
        onError: (err) =>
          toast.error(err instanceof Error ? err.message : "Share update failed"),
      },
    );
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          size="sm"
          variant="ghost"
          aria-label={`Manage sharing for ${path}`}
          className="gap-1.5 font-mono text-xs"
        >
          <Share2 className="size-4" />
          {count > 0 ? `Shared · ${count}` : "Share"}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72">
        <p className="mb-2 font-mono text-xs uppercase tracking-wider text-muted-foreground">
          Visible to clients
        </p>
        {sharing.query.isLoading && (
          <div className="flex items-center gap-2 py-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" /> Loading…
          </div>
        )}
        {sharing.query.isError && (
          <p className="py-2 text-sm text-destructive">
            Couldn't load share manifests.
          </p>
        )}
        <ul className="flex flex-col gap-2">
          {states.map(({ client, exact, viaPrefix }) => (
            <li key={client.id} className="flex items-center gap-2">
              <Checkbox
                id={`share-${client.id}-${path}`}
                checked={exact || viaPrefix}
                disabled={busy || (viaPrefix && !exact)}
                onCheckedChange={(checked) =>
                  onToggle(client.id, checked === true)
                }
              />
              <label
                htmlFor={`share-${client.id}-${path}`}
                className="flex-1 cursor-pointer truncate text-sm"
                title={client.name}
              >
                {client.name}
              </label>
              {viaPrefix && !exact && (
                <span
                  className="font-mono text-fs-label uppercase text-muted-foreground"
                  title="Covered by a folder rule in this client's manifest — edit the manifest to change it"
                >
                  folder rule
                </span>
              )}
            </li>
          ))}
          {!sharing.query.isLoading && states.length === 0 && (
            <li className="text-sm text-muted-foreground">
              No active clients registered.
            </li>
          )}
        </ul>
        {busy && (
          <p className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
            <Loader2 className="size-3 animate-spin" /> Saving…
          </p>
        )}
      </PopoverContent>
    </Popover>
  );
}
